import React, { useState } from "react";
import { CheckIcon } from "lucide-react";
import { buttonClass } from "../shared/Button";
import { SectionHeader } from "../shared/SectionHeader";
import { Reveal } from "../shared/Reveal";

// [Lead Gen] [SEO/GEO] Open, interactive checklist for the next-home persona. Optional emailed copy is SIMULATED.
const guideSections = [{
  id: "block",
  title: "Your block",
  items: ["Street address or lot number", "Block width and depth", "Which way the block faces", "Any slope or retaining", "Easements, sewer or power lines", "Existing home to demolish?"]
}, {
  id: "chapter",
  title: "Your next chapter",
  items: ["Will single-level living matter in future?", "Rooms for visiting family or grandchildren", "A lock-and-leave home for travel", "Home office or hobby space", "How much garden you want to maintain", "Must-haves versus nice-to-haves"]
}, {
  id: "inclusions",
  title: "Comparing quotes",
  items: ["What’s standard in each quote", "What’s listed as an upgrade", "How site costs are treated", "Provisional sums and allowances", "What’s excluded", "How variations are priced"]
}, {
  id: "questions",
  title: "Ask any builder",
  items: ["Who will I deal with day to day?", "How will you keep me updated?", "What’s in the pre-construction stage?", "What affects the build timeframe?", "What happens at handover?", "Can I speak to past clients?"]
}];
const totalItems = guideSections.reduce((sum, section) => sum + section.items.length, 0);
export function PlanningGuide() {
  const [activeId, setActiveId] = useState(guideSections[0].id);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "error" | "done">("idle");
  const active = guideSections.find(section => section.id === activeId) ?? guideSections[0];
  const done = Object.values(checked).filter(Boolean).length;
  const toggle = (key: string) => setChecked(current => ({
    ...current,
    [key]: !current[key]
  }));
  const sendCopy = (event: React.FormEvent) => {
    event.preventDefault();
    setEmailStatus(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? "done" : "error");
  };
  return <section aria-labelledby="guide-heading" className="bg-paper-deep/60 py-20 md:py-28" data-id="element-314" data-mp="3hkyy5g37xajl">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-315" data-mp="3cjj9zf5qunwz">
        <SectionHeader id="guide-heading" title="Planning your next home? Start here." intro="A practical checklist to work through before you speak to any builder. Tick items off as you go. Nothing is saved or sent." data-id="element-316" data-mp="38ifvox3cgzxv" />

        <Reveal className="mx-auto mt-12 max-w-4xl" data-id="element-317" data-mp="2yq1rlxosnh8j">
          <div className="rounded-[32px] border border-line bg-white p-5 md:p-8" data-id="element-318" data-mp="2tumh9fpzw3gv">
            <div role="tablist" aria-label="Checklist sections" className="grid grid-cols-2 gap-2 md:grid-cols-4" data-id="element-319" data-mp="2ougmhqofi0uk">
              {guideSections.map(section => <button key={section.id} role="tab" aria-selected={section.id === activeId} aria-controls="guide-panel" id={`guide-tab-${section.id}`} onClick={() => setActiveId(section.id)} className={`min-h-[48px] whitespace-nowrap rounded-full border px-3 text-[15px] font-semibold transition-[background-color,color,border-color] duration-150 ease-out ${section.id === activeId ? "border-charcoal bg-charcoal text-paper" : "border-line bg-paper text-charcoal hover:border-charcoal"}`} data-id="element-320" data-mp="10akwatd2yqql">
                  {section.title}
                </button>)}
            </div>

            <ul id="guide-panel" role="tabpanel" aria-labelledby={`guide-tab-${active.id}`} className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-321" data-mp="vbrg2t6eslmq">
              {active.items.map(item => {
              const key = `${active.id}-${item}`;
              const isChecked = !!checked[key];
              return <li key={key} data-id="element-322" data-mp="24le7m3cbf0ui">
                    <label className={`flex min-h-[60px] cursor-pointer items-center gap-4 rounded-2xl border px-5 py-3 text-[16px] transition-colors duration-150 ease-out ${isChecked ? "border-ocean bg-paper" : "border-line hover:border-charcoal/40"}`} data-id="element-323" data-mp="24kmdkwyd9b6c">
                      <input type="checkbox" checked={isChecked} onChange={() => toggle(key)} className="peer sr-only" data-id="element-324" data-mp="29j79wlahzapi" />
                      <span aria-hidden="true" className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ease-out peer-focus-visible:ring-2 peer-focus-visible:ring-ocean ${isChecked ? "border-ocean bg-ocean text-charcoal" : "border-slate"}`} data-id="element-325" data-mp="2o70pi5zl2d83">
                        {isChecked && <CheckIcon className="h-4 w-4" data-id="element-326" data-mp="2saqdat8foqs2" />}
                      </span>
                      <span className={isChecked ? "text-slate line-through" : ""} data-id="element-327" data-mp="12vsd9b29tdri">{item}</span>
                    </label>
                  </li>;
            })}
            </ul>

            <div className="mt-6" data-id="element-328" data-mp="39kkhdytht9ur">
              <div className="flex items-center justify-between text-[14px] text-slate" data-id="element-329" data-mp="39llue8u2k6f4">
                <span data-id="element-330" data-mp="3ekgmy2hu99cf">Your progress</span>
                <span aria-live="polite" data-id="element-331" data-mp="30rhbcbl6fza5">{done} of {totalItems} ticked</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-paper-deep" data-id="element-332" data-mp="34s14z56jkkrj">
                <div className="h-full rounded-full bg-ocean transition-[width] duration-300 ease-out" style={{
                width: `${done / totalItems * 100}%`
              }} data-id="element-333" data-mp="2qsrsoglgiasu" />
              </div>
            </div>

            <form onSubmit={sendCopy} noValidate className="mt-8 border-t border-line pt-6 text-center" data-id="element-334" data-mp="2hs3bjd0rnubq">
              <label htmlFor="guide-email" className="block text-[16px] font-semibold" data-id="element-335" data-mp="2ltorx8cieqqo">Email me this checklist <span className="font-normal text-slate" data-id="element-336" data-mp="g0qhjj0c76ug">(optional)</span></label>
              <div className="mx-auto mt-3 flex max-w-lg flex-col gap-2 sm:flex-row" data-id="element-337" data-mp="fym3w9cmytgz">
                <input id="guide-email" type="email" value={email} onChange={event => {
                setEmail(event.target.value);
                setEmailStatus("idle");
              }} aria-invalid={emailStatus === "error"} aria-describedby="guide-email-status" placeholder="you@example.com" className="min-h-[52px] min-w-0 flex-1 rounded-full border border-line bg-white px-5 text-base focus:border-charcoal focus:outline-none focus:ring-2 focus:ring-ocean" data-id="element-338" data-mp="k1isqhsrrltd" />
                <button type="submit" className={buttonClass("secondary")} data-id="element-339" data-mp="27pg7edcrbnms">Send checklist</button>
              </div>
              <p id="guide-email-status" role="status" className={`mt-2 text-sm ${emailStatus === "error" ? "text-alert" : "text-slate"}`} data-id="element-340" data-mp="22vlqtisg7orx">
                {emailStatus === "error" && "Enter a valid email address."}
                {emailStatus === "done" && "Demo only. Nothing has been sent."}
                {emailStatus === "idle" && "Demo: delivery is simulated. Used only to send this checklist."}
              </p>
            </form>
          </div>
        </Reveal>
      </div>
    </section>;
}